import { Checkbox, checkboxStates } from "@visiblethread/react-components";
import clsx from "clsx";
import { map, without } from "lodash";
import { number, object, oneOfType, shape, string } from "prop-types";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedRowIds } from "slices/proposalOutline/outlineSlice";
import {
  selectProposalOutlineSections,
  selectSelectedRowIds,
} from "slices/proposalOutline/selectors";
import { getChildOutlineSectionsByIndex } from "utils/proposals";

import classes from "./CheckboxCell.module.scss";

const CheckboxCell = ({ row }) => {
  const dispatch = useDispatch();
  const selectedRowIds = useSelector(selectSelectedRowIds);
  const sections = useSelector(selectProposalOutlineSections);
  const { index, id: rowId } = row;

  const [childIds, state] = useMemo(() => {
    const children = getChildOutlineSectionsByIndex(sections, index);
    const childrenIds = map(children, "id");
    const isChecked = selectedRowIds.includes(rowId);
    const hasChildren = childrenIds.length > 0;
    const initialState = isChecked
      ? checkboxStates.checked
      : checkboxStates.empty;

    if (!hasChildren) {
      return [childrenIds, initialState];
    }
    const otherSelectedRowIds = without(selectedRowIds, rowId);
    const areSomeChildrenSelected =
      isChecked &&
      otherSelectedRowIds.length > 0 &&
      !childrenIds.every((id) => otherSelectedRowIds.includes(id));

    return [
      childrenIds,
      areSomeChildrenSelected ? checkboxStates.indeterminate : initialState,
    ];
  }, [sections, index, rowId, selectedRowIds]);

  const onToggle = () => {
    const isRowSelected = selectedRowIds.includes(rowId);
    const hasChildren = childIds.length > 0;

    // Case 1: Row not selected, no children - just select the row
    if (!isRowSelected && !hasChildren) {
      return dispatch(setSelectedRowIds([...selectedRowIds, rowId]));
    }

    // Case 2: Row already selected, no children - unselect the row
    if (isRowSelected && !hasChildren) {
      return dispatch(setSelectedRowIds(without(selectedRowIds, rowId)));
    }

    // Case 3: Row not selected, has children - select row and all children
    if (!isRowSelected && hasChildren) {
      return dispatch(
        setSelectedRowIds([...selectedRowIds, rowId, ...childIds])
      );
    }

    // Cases 4 & 5: Row already selected and has children
    if (isRowSelected && hasChildren) {
      const allChildrenSelected = childIds.every((childId) =>
        selectedRowIds.includes(childId)
      );

      if (allChildrenSelected) {
        // Case 5: All children selected - unselect row and all children
        const idsToRemove = [rowId, ...childIds];
        return dispatch(
          setSelectedRowIds(
            selectedRowIds.filter((id) => !idsToRemove.includes(id))
          )
        );
      } else {
        // Case 4: Some children selected - select row and all children
        const newSelectedIds = [...selectedRowIds];
        childIds.forEach((childId) => {
          if (!newSelectedIds.includes(childId)) {
            newSelectedIds.push(childId);
          }
        });
        return dispatch(setSelectedRowIds(newSelectedIds));
      }
    }
  };

  return (
    <Checkbox
      className={clsx(classes.checkbox_cell, {
        [classes["checkbox_cell--checked"]]: state === checkboxStates.checked,
        [classes["checkbox_cell--indeterminate"]]:
          state === checkboxStates.indeterminate,
      })}
      handleChange={onToggle}
      state={state}
    />
  );
};

CheckboxCell.propTypes = {
  row: shape({
    id: oneOfType([number, string]).isRequired,
    original: object.isRequired,
  }),
};

export default CheckboxCell;
